import { ChangeEvent, useMemo, useState } from 'react';

import { ProductsSequence, type ProductsSequenceProduct } from 'common/contracts';
import { EProductPriority } from 'constants/enums';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import Input from 'components/Input/Input';
import { ProductsSequenceTable } from 'components/ProductsSequenceTable';

import {
  MAX_PRODUCTS_PER_MILESTONE,
  MILESTONE_PRODUCT_TYPE_OPTIONS
} from '../../ProgressBars.consts';
import type { IProgressBarTabProps } from '../../ProgressBars.types';

interface ICardProps extends Pick<IProgressBarTabProps, 'formikProps'> {
  productSequenceItem: ProductsSequence;
  sequenceIndex: number;
  isMilestoneDesign: boolean;
  isFinalReward: boolean;
}

const MILESTONE_MAX_PRODUCTS = MAX_PRODUCTS_PER_MILESTONE;
const MISSION_MAX_PRODUCTS = 1;

const MilestoneCard: React.FC<ICardProps> = ({
  formikProps,
  productSequenceItem,
  sequenceIndex,
  isMilestoneDesign,
  isFinalReward
}) => {
  const { setFieldValue, handleBlur, values } = formikProps;
  const [quantities, setQuantities] = useState<Record<number, string>>({});

  const updateSubOfferData = (updatedData: Partial<ProductsSequence>): void => {
    const updatedSequenceItem = {
      ...values.productsSequence[sequenceIndex],
      ...updatedData
    };
    setFieldValue(`productsSequence[${sequenceIndex}]`, updatedSequenceItem);
  };

  const persistedProducts: ProductsSequenceProduct[] =
    values.productsSequence[sequenceIndex]?.products || [];

  const emptyRow = useMemo<ProductsSequenceProduct[]>(
    () => [
      isMilestoneDesign
        ? { productId: '', quantity: 1, priority: EProductPriority.MAIN }
        : { productId: '', quantity: 1 }
    ],
    [isMilestoneDesign]
  );

  const products: ProductsSequenceProduct[] =
    persistedProducts.length > 0 ? persistedProducts : emptyRow;

  const handleProductChange = (
    productIndex: number,
    productId: string | null
  ): void => {
    updateSubOfferData({
      products: products.map((product, index) =>
        index === productIndex
          ? { ...product, productId: productId || undefined }
          : product
      )
    });
  };

  const handlePriorityChange = (
    productIndex: number,
    priority: EProductPriority
  ): void => {
    updateSubOfferData({
      products: products.map((product, index) =>
        index === productIndex ? { ...product, priority } : product
      )
    });
  };

  const handleQuantityChange = (
    e: ChangeEvent<HTMLInputElement>,
    productIndex: number
  ): void => {
    const inputValue = e.target.value;
    const newQuantity = inputValue.length !== 0 ? Number(inputValue) : 0;
    if (newQuantity < 1 && inputValue !== '') return;

    updateSubOfferData({
      products: products.map((product, index) =>
        index === productIndex ? { ...product, quantity: newQuantity } : product
      )
    });
    setQuantities((prev) => ({ ...prev, [productIndex]: inputValue }));
  };

  const handleRemoveProduct = (productIndex: number): void => {
    updateSubOfferData({
      products: products.filter((_, index) => index !== productIndex)
    });
  };

  const handleAddProduct = (): void => {
    updateSubOfferData({
      products: [
        ...products,
        { productId: '', quantity: 1, priority: EProductPriority.MAIN }
      ]
    });
  };

  const handleBarSizeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    updateSubOfferData({ barSize: Number(e.target.value) });
  };

  const handleDeleteProductSequence = (index: number): void => {
    const updatedSequence = values.productsSequence
      .filter((_: ProductsSequence, i: number) => i !== index)
      .map((item: ProductsSequence, i: number) => ({ ...item, index: i + 1 }));

    setFieldValue('productsSequence', updatedSequence);
  };

  const handleDuplicateProductSequence = (index: number): void => {
    const newProductsSequence = [...values.productsSequence];
    const itemToDuplicate = newProductsSequence[index];

    const duplicatedSequence = {
      ...itemToDuplicate,
      index: newProductsSequence.length + 1,
      products: itemToDuplicate.products.map(
        (product: ProductsSequenceProduct) => ({ ...product })
      )
    };

    const updatedSequence = [
      ...newProductsSequence.slice(0, index + 1),
      duplicatedSequence,
      ...newProductsSequence.slice(index + 1)
    ].map((item, i) => ({ ...item, index: i + 1 }));

    setFieldValue('productsSequence', updatedSequence);
  };

  const stepLabel = isMilestoneDesign ? 'Milestone' : 'Mission';
  const cardTitle = `${stepLabel} ${productSequenceItem.index}${
    isMilestoneDesign && isFinalReward ? ' (Final Reward)' : ''
  }`;
  const barSizeLabel = isMilestoneDesign ? 'Bar Pts' : 'Bar size';

  return (
    <div className="flex flex-col p-4 bg-base-white border border-base-border rounded-lg min-w-[528px] w-[530px] mb-6 text-base-foreground">
      <div className="flex justify-between items-center mb-2">
        <p className="text-xl font-semibold">{cardTitle}</p>
        <ActionCell
          rowData={productSequenceItem}
          actions={[
            {
              label: 'Duplicate',
              handler: () => handleDuplicateProductSequence(sequenceIndex)
            },
            {
              label: 'Delete',
              handler: () => handleDeleteProductSequence(sequenceIndex),
              disabled: values.productsSequence?.length === 1
            }
          ]}
        />
      </div>

      <div className="w-1/2 mb-4">
        <Input
          label={barSizeLabel}
          name={`productsSequence[${sequenceIndex}].barSize`}
          type="number"
          value={values.productsSequence[sequenceIndex]?.barSize || 1}
          onChange={handleBarSizeChange}
          onBlur={handleBlur}
          dataTestId={`progressBarSizeInput-${sequenceIndex}`}
          className="w-full rounded-md py-2 text-base-foreground"
        />
      </div>

      <ProductsSequenceTable
        products={products}
        sequenceIndex={sequenceIndex}
        onProductChange={handleProductChange}
        onPriorityChange={handlePriorityChange}
        onQuantityChange={handleQuantityChange}
        onRemoveProduct={handleRemoveProduct}
        onAddProduct={handleAddProduct}
        localQuantities={quantities}
        priorityOptions={MILESTONE_PRODUCT_TYPE_OPTIONS}
        priorityColumnLabel="Product Type"
        maxProducts={
          isMilestoneDesign ? MILESTONE_MAX_PRODUCTS : MISSION_MAX_PRODUCTS
        }
        addButtonLabel="Add Product"
        hideSelectedProducts={isMilestoneDesign}
        showPriorityColumn={isMilestoneDesign}
        showAddButton={isMilestoneDesign}
        showRemoveButton={isMilestoneDesign}
        testIds={{
          productRowPrefix: `progressBarProductRow-${sequenceIndex}`,
          productButtonPrefix: `progressBarProductInput-${sequenceIndex}-`,
          productListId: `dashProgressBarProductList-${sequenceIndex}`,
          priorityButtonPrefix: `progressBarProductTypeInput-${sequenceIndex}-`,
          priorityListId: `dashProgressBarProductTypeList-${sequenceIndex}`,
          quantityInputPrefix: `progressBarProductQuantityInput-${sequenceIndex}`,
          deleteButtonPrefix: `progressBarProductDelete-${sequenceIndex}`,
          addButtonId: `progressBarAddProduct-${sequenceIndex}`
        }}
      />
    </div>
  );
};

export default MilestoneCard;
